import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIban, meUpdateSchema } from '@vuokramani/shared';
import { pageStyles as s } from '../../components/page.tsx';
import { Alert, Button, Notice, Panel, TextField } from '../../components/ui.tsx';
import { errorKey } from '../../lib/api.ts';
import { useMe } from '../../lib/auth.ts';
import { useUpdateMe } from '../../lib/properties.ts';
import c from './contact.module.css';

/**
 * Nimi ja puhelin sekä vuokranantajalle valinnat, näkyvätkö sähköposti ja puhelin
 * vuokralaisille. Nimi näkyy vuokralaiselle aina.
 */
export function ContactSettings() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const update = useUpdateMe();
  const [name, setName] = useState(me?.name ?? '');
  const [phone, setPhone] = useState(me?.phone ?? '');
  const [showEmail, setShowEmail] = useState(me?.showEmailToTenants ?? false);
  const [showPhone, setShowPhone] = useState(me?.showPhoneToTenants ?? false);
  const [iban, setIban] = useState(me?.iban ? formatIban(me.iban) : '');
  const [error, setError] = useState<string>();

  if (!me) return null;
  const landlord = me.roles.landlord;

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = meUpdateSchema.safeParse({
      name,
      phone,
      ...(landlord ? { showEmailToTenants: showEmail, showPhoneToTenants: showPhone, iban } : {}),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'errors.generic');
      return;
    }
    setError(undefined);
    update.mutate(parsed.data);
  }

  return (
    <section>
      <h2 className={s.sectionTitle}>{t('account.contact')}</h2>
      <Panel>
        <form className={s.form} onSubmit={submit} noValidate style={{ maxWidth: 520 }}>
          <div className={s.formRow}>
            <TextField
              label={t('account.name')}
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label={t('edit.phone')}
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              error={error === 'errors.phone' ? t(error) : undefined}
            />
          </div>
          {landlord && (
            <TextField
              label={t('account.iban')}
              hint={t('account.ibanHint')}
              autoComplete="off"
              value={iban}
              onChange={(e) => setIban(e.target.value)}
              error={error === 'errors.iban' ? t(error) : undefined}
            />
          )}
          {landlord && (
            <fieldset className={c.toggles}>
              <legend className={c.legend}>{t('account.visibleToTenants')}</legend>
              <p className={c.hint}>{t('account.visibleHint')}</p>
              <label className={c.toggle}>
                <input
                  type="checkbox"
                  checked={showEmail}
                  onChange={(e) => setShowEmail(e.target.checked)}
                />
                <span>{t('account.showEmail', { email: me.email })}</span>
              </label>
              <label className={c.toggle}>
                <input
                  type="checkbox"
                  checked={showPhone}
                  onChange={(e) => setShowPhone(e.target.checked)}
                />
                <span>{t('account.showPhone')}</span>
              </label>
            </fieldset>
          )}
          {update.isError && <Alert>{t(errorKey(update.error))}</Alert>}
          {update.isSuccess && <Notice>{t('account.saved')}</Notice>}
          <div>
            <Button type="submit" variant="primary" disabled={update.isPending}>
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Panel>
    </section>
  );
}
